import {XAXisComponentOption, YAXisComponentOption} from 'echarts';
import {LineChart, LineSeriesOption} from 'echarts/charts';
import {
    DataZoomComponent,
    DataZoomComponentOption,
    DatasetComponent,
    DatasetComponentOption,
    GridComponent,
    GridComponentOption,
    LegendComponent,
    LegendComponentOption,
    TitleComponent,
    TitleComponentOption,
    TooltipComponent,
    TooltipComponentOption,
    TransformComponent,
} from 'echarts/components';
import {use} from 'echarts/core';
import {CanvasRenderer} from 'echarts/renderers';
import Vue from 'vue';
import VChart from 'vue-echarts';
import {Component, Prop} from 'vue-property-decorator';

type MyChartOption = echarts.ComposeOption<
    | TitleComponentOption
    | TooltipComponentOption
    | LegendComponentOption
    | GridComponentOption
    | XAXisComponentOption
    | YAXisComponentOption
    | LineSeriesOption
    | DataZoomComponentOption
    | DatasetComponentOption
>;

use([CanvasRenderer, LineChart, TitleComponent, TooltipComponent, GridComponent, LegendComponent, DataZoomComponent, DatasetComponent, TransformComponent]);

export interface RawChartDataItem {
    Time: string | number | Date;
    Month?: string;
    [key: string]: string | number | Date | undefined;
}

@Component({
    name: 'TimeSeriesLineChartComponent',
    components: {
        VChart,
    },
})
export default class TimeSeriesLineChartComponent extends Vue {
    @Prop({
        type: Array as () => RawChartDataItem[],
        required: true,
        validator: (value: unknown): boolean => Array.isArray(value),
    })
    readonly rawData!: RawChartDataItem[];

    @Prop({type: String, required: true, default: 'Time'})
    readonly timeKey!: string;

    @Prop({
        type: Array as () => string[],
        required: true,
    })
    readonly valueKeys!: string[];

    @Prop({
        type: Object as () => Partial<MyChartOption>,
        default: () => ({}),
    })
    readonly extraOptions!: Partial<MyChartOption>;

    get chartOptions(): MyChartOption {
        const seriesOptions: LineSeriesOption[] = this.valueKeys.map(key => ({
            name: key,
            type: 'line',
            smooth: true,
            sampling: 'lttb',
            areaStyle: {},
            encode: {
                x: this.timeKey,
                y: key,
                itemName: this.timeKey,
                tooltip: [this.timeKey, key],
            },
        }));

        const baseOptions: MyChartOption = {
            dataset: {
                source: this.rawData,
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross',
                    label: {backgroundColor: '#6a7985'},
                },
            },
            legend: {
                top: 'top',
                data: this.valueKeys,
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '10%',
                containLabel: true,
            },
            xAxis: {
                type: 'time',
                axisLabel: {
                    formatter: (value: number): string => {
                        const date = new Date(value);
                        return date.toLocaleDateString();
                    },
                    hideOverlap: true,
                },
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    formatter: (value: number): string => {
                        return value.toLocaleString();
                    },
                },
            },
            series: seriesOptions,
            dataZoom: [
                {
                    type: 'slider',
                    xAxisIndex: 0,
                    start: 0,
                    end: 100,
                    bottom: '5px',
                    height: 20,
                    showDetail: false,
                },
                {
                    type: 'inside',
                    xAxisIndex: 0,
                    start: 0,
                    end: 100,
                },
            ],
        };

        const mergedOptions: MyChartOption = {
            ...baseOptions,
            ...this.extraOptions,
        };

        return mergedOptions;
    }
}
